import React, { useContext } from "react";
import { Link } from "react-router-dom";
import {
    ArrowLeft,
    Dot,
    ExternalLink,
    MapPin,
    RefreshCw,
    Timer,
} from "lucide-react";
import { useParams } from "react-router";
import { IntlContext } from "context";
import { useAd, useAdApplications } from "hooks/ad";
import { AdFrequency } from "types/ad";
import { SUBCATEGORY_TO_CATEGORY, SubCategory } from "types/subCategory";
import { buildRoute } from "routes";
import { formatAdServiceLocation } from "helpers/formatAdServiceLocation";
import { Badge } from "components/ui/badge";
import { Button } from "components/ui/button";
import { Loader } from "ui/Loader";
import { getCategoryStyle } from "../../MyServices/categoryStyle";
import ApplicationItem, { type AdApplicationRecord } from "./Item";

const AdApplications: React.FC = () => {
    const intl = useContext(IntlContext);
    const { id } = useParams();
    const { data: ad, isLoading: loadingAd } = useAd(id);
    const { data: applicationsData, isLoading: loadingApplications } = useAdApplications(id);
    const applications = (applicationsData ?? []) as AdApplicationRecord[];

    if (loadingAd || !ad) {
        return <Loader />;
    }

    const categoryKey = ad.category.toString();
    const { Icon, boxClass, iconClass } = getCategoryStyle(categoryKey);
    const subCategory = ad.subcategory as SubCategory | undefined;
    const subcategoryLabel = subCategory
        ? intl.formatMessage(
              `subcategory.${SUBCATEGORY_TO_CATEGORY[subCategory].toLocaleLowerCase()}.${subCategory.toLocaleLowerCase()}.label`,
          )
        : "";
    const title =
        ad.title?.trim() ||
        (subCategory
            ? intl.formatMessage(
                  `subcategory.${SUBCATEGORY_TO_CATEGORY[subCategory].toLocaleLowerCase()}.${subCategory.toLocaleLowerCase()}.label`,
              )
            : intl.formatMessage(`category.${categoryKey.toLocaleLowerCase()}`));
    const location = formatAdServiceLocation(ad);
    const isRecurring = ad.frequency === AdFrequency.RECURRING;
    const publishedLabel = intl.formatDate(ad.created_at, {
        day: "numeric",
        month: "long",
        year: "numeric",
    });

    return (
        <div className="mx-16 my-12 space-y-6">
            <Link
                to={buildRoute.myAdDetails(ad.id)}
                className="inline-flex items-center gap-2 text-sm font-medium text-slate-600 transition-colors hover:text-primary"
            >
                <ArrowLeft className="h-4 w-4" strokeWidth={2.25} />
                {intl.formatMessage("profile.my_ads.applications.back")}
            </Link>

            <article className="rounded-2xl border border-gray-200 bg-white p-4 sm:p-5">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                    <div className="flex min-w-0 items-start gap-4">
                        <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl ${boxClass}`}>
                            <Icon className={`h-5 w-5 ${iconClass}`} strokeWidth={2} />
                        </div>

                        <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                                <h1 className="text-lg font-extrabold text-gray-900">{title}</h1>
                                <Badge
                                    variant="frequency"
                                    palette={isRecurring ? "violet" : "orange"}
                                    className="normal-case tracking-normal"
                                >
                                    {isRecurring ? <RefreshCw /> : <Timer />}
                                    {isRecurring
                                        ? intl.formatMessage("ad.card.frequency_recurring")
                                        : intl.formatMessage("ad.card.frequency_one_time")}
                                </Badge>
                            </div>

                            <div className="mt-2 flex flex-wrap items-center text-sm text-slate-500">
                                {subcategoryLabel && (
                                    <>
                                        <span>{subcategoryLabel}</span>
                                        <Dot className="h-4 w-4" />
                                    </>
                                )}
                                <span className="inline-flex items-center gap-1">
                                    <MapPin className="h-4 w-4 shrink-0" />
                                    <span className="truncate">{location}</span>
                                </span>
                                <Dot className="h-4 w-4" />
                                <span>
                                    {intl.formatMessage("profile.my_ads.details.published_on", {
                                        date: publishedLabel,
                                    })}
                                </span>
                            </div>
                        </div>
                    </div>

                    <Button variant="outline" size="sm" className="shrink-0 rounded-xl" asChild>
                        <Link to={buildRoute.ad(ad.id)} target="_blank" rel="noreferrer">
                            <ExternalLink className="h-4 w-4" />
                            {intl.formatMessage("ad.card.view_public_ad")}
                        </Link>
                    </Button>
                </div>

                {ad.description && (
                    <div className="mt-4 rounded-2xl border border-gray-200 bg-slate-50 p-4 text-sm text-slate-600">
                        {ad.description}
                    </div>
                )}
            </article>

            <section className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm sm:p-8">
                <div className="mb-6">
                    <h2 className="text-2xl font-bold text-gray-900">
                        {intl.formatMessage("profile.my_ads.applications.title")}
                    </h2>
                    <p className="mt-1 text-sm text-slate-500">
                        {loadingApplications
                            ? intl.formatMessage("profile.my_ads.applications.loading")
                            : intl.formatMessage("profile.my_ads.applications.count", {
                                  count: applications.length,
                              })}
                    </p>
                </div>

                {loadingApplications ? (
                    <Loader />
                ) : applications.length === 0 ? (
                    <div className="rounded-lg border border-dashed border-gray-200 bg-slate-50 px-6 py-12 text-center">
                        <p className="text-sm text-slate-600">
                            {intl.formatMessage("profile.my_ads.applications.empty")}
                        </p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                        {applications.map((application) => (
                            <ApplicationItem
                                key={`${application.service_id}-${application.ad_id}-${application.created_at}`}
                                application={application}
                            />
                        ))}
                    </div>
                )}
            </section>
        </div>
    );
};

export default AdApplications;
